import * as React from "react"
import {
  LayoutDashboardIcon,
  AppWindowIcon,
  UsersIcon,
  CalendarIcon,
  WrenchIcon,
  PlugIcon,
  MessageSquareIcon,
  TruckIcon,
  InstagramIcon,
  FacebookIcon,
  PhoneIcon,
  MessageCircleIcon,
  YoutubeIcon,
  Building2Icon,
  Settings2Icon,
} from "lucide-react"
import { Link } from "@inertiajs/react"

import { NavCollapsible } from "@/components/nav-collapsible"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  role: string
}

interface AppSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User
}

export function AppSidebar({ user, ...props }: AppSidebarProps) {
  const shopNav: Array<{
    title: string
    url: string
    icon: typeof LayoutDashboardIcon
    activePattern?: string
  }> = [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: LayoutDashboardIcon,
    },
    {
      title: "Shop Leads",
      url: "/leads",
      icon: WrenchIcon,
      activePattern: "/leads",
    },
    {
      title: "Calls",
      url: "/shop/calls",
      icon: PhoneIcon,
      activePattern: "/shop/calls",
    },
    {
      title: "Meta/Facebook",
      url: "/instagram/conversations",
      icon: FacebookIcon,
      activePattern: "/(instagram|messenger|whatsapp)/conversations",
    },
    {
      title: "YouTube",
      url: "/youtube/videos",
      icon: YoutubeIcon,
      activePattern: "/youtube/videos",
    },
    {
      title: "Google Calendar",
      url: "/shop/google_calendar",
      icon: CalendarIcon,
      activePattern: "/shop/google_calendar",
    },
  ]

  const recruitingNav: Array<{
    title: string
    url: string
    icon: typeof LayoutDashboardIcon
    activePattern?: string
  }> = [
    {
      title: "Dashboard",
      url: "/recruiting/dashboard",
      icon: LayoutDashboardIcon,
      activePattern: "/recruiting/dashboard",
    },
    {
      title: "Driver Leads",
      url: "/driver_leads",
      icon: TruckIcon,
      activePattern: "/driver_leads",
    },
    {
      title: "Calls",
      url: "/recruiting/calls",
      icon: PhoneIcon,
      activePattern: "/recruiting/calls",
    },
    {
      title: "Meta/Facebook",
      url: "/recruiting/messages",
      icon: FacebookIcon,
      activePattern: "/(recruiting/messages|recruiting/messenger_messages|whatsapp/recruiting/messages)",
    },
    {
      title: "Google Calendar",
      url: "/recruiting/google_calendar",
      icon: CalendarIcon,
      activePattern: "/recruiting/google_calendar",
    },
  ]

  const adminNav: Array<{
    title: string
    url: string
    icon: typeof LayoutDashboardIcon
    activePattern?: string
  }> = [
    {
      title: "Users",
      url: "/admin/users",
      icon: UsersIcon,
      activePattern: "/admin/users",
    },
    {
      title: "Integrations",
      url: "/admin/integrations",
      icon: PlugIcon,
      activePattern: "/admin/integrations",
    },
    {
      title: "Console",
      url: "/admin/console",
      icon: AppWindowIcon,
      activePattern: "/admin/console",
    },
  ]

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent">
              <Link href="/dashboard">
                <div className="flex aspect-square size-8 items-center justify-center rounded-full bg-gradient-to-br from-[#5589ed] to-[#4a7dd9] text-white shadow-md">
                  <svg className="size-5" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">AI CRM</span>
                  <span className="truncate text-xs text-muted-foreground">by Cayu</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavCollapsible
          title="Modern Shop"
          sectionKey="modern-shop"
          icon={Building2Icon}
          items={shopNav}
          defaultExpanded={true}
        />
        <NavCollapsible
          title="Modern Nomad"
          sectionKey="modern-nomad"
          icon={TruckIcon}
          items={recruitingNav}
          defaultExpanded={false}
        />
        {user.role === 'admin' && (
          <NavCollapsible
            title="Admin Console"
            sectionKey="admin-console"
            icon={Settings2Icon}
            items={adminNav}
            defaultExpanded={false}
          />
        )}
      </SidebarContent>
    </Sidebar>
  )
}
